
import {concat as observableConcat,  Subscription ,  Observable, BehaviorSubject, Subject, of } from 'rxjs';
import { Component, ElementRef, AfterViewInit, ViewChild, OnDestroy, EventEmitter, Output } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { first, tap, finalize } from 'rxjs/operators';


import { BaseControlComponent } from '../base-control/base-control.component';

import { AuthService } from "../../../services/auth.service";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { DocumentManagementService, IUploadFile, IAzureBlobSasToken } from "../../../common/services/document-management.service";
import { LoaderService } from "../../../common/services/loader.service";
import { BlobDownloadRequest } from '../../../common/models/onBaseIntegrationAndMapping.model';

import { RadioButtonOption } from "../../../common/models/flow.dto";
import { UserIdentity } from "../../../models/user-identity.model";
import { ContextInteractionService } from '../../services/context-interaction.service';

@Component({
    selector: 'app-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    standalone: false
})
export class FileUploadComponent extends BaseControlComponent implements AfterViewInit, OnDestroy {
    static componentName = 'FileUploadComponent';
    public formControl: AbstractControl;
    public hasFilesToDrop: boolean = false;
    public files: Array<IUploadFile> = new Array<IUploadFile>();
    public fileSubscription = new Subscription();
    public file: IUploadFile;
	public selectedOption: RadioButtonOption;

	private _whitelist: string[] = [];
	private whitelistMessage = "You have uploaded a disallowed file type.  The file upload has been cancelled. <br /> The allowed file types are: <000>. <br /> NOTE: RICS is no longer accepting Word 97-2003 (.doc) format files. <br />  Please select a different file and try again.  "
	
    deleteDialog: Dialog = new Dialog("Are you sure you want to delete this file?", [new DialogButton("Yes", true, () => this.onDialogConfirm()), new DialogButton("No", false, () =>  this.onDialogClose())]);
    disallowedDialog: Dialog = new Dialog("", [new DialogButton("OK", false, () =>  this.onDialogClose())])
    get deliverText(): string {
        return this.componentData.config.deliverText;
    }

    get disable(): boolean {
        return this.isEdit || !this.isEnabled;
    }

    get disableScanView(): boolean {
        return this.disable || this.isScanView;
    }

    get hasFileListing(): boolean {
        if (!this.files)
            return false;
        return this.files.length > 0;
    }

    get isScanView(): boolean {
        return this.flowComponent === undefined;
    }

    get selectedOptions(): RadioButtonOption[] {
        if (this.componentData.config.useRadioButtons) {
            const selected = this.componentData.config.radioOptions.filter(r => r.included);
            return selected;
        }
        return [];
    }

    get showText(): boolean {
        let show: boolean = false;
        if (this.componentData.config.useRadioButtons && this.selectedOption) {
            show = this.selectedOption.showsText;
        }
        return show;
    }

    get showUpload(): boolean {
        let show: boolean = false;
        if (this.componentData.config.useRadioButtons && !this.isScanView) {
            if (this.selectedOption) {
            show = this.selectedOption.showsUpload || this.hasFileListing;
            }
        } else {
            show = true;
        }
        return show;
    }

    get useRadioButtons() : boolean {
        return this.componentData && this.componentData.config && this.componentData.config.useRadioButtons;
    }

    get user(): UserIdentity {
        return this._authService.User;
    }

    @Output() fileUploaded = new EventEmitter<any>(); //Used by Scanning UI to force update context.  Do NOT remove or leave undefined!
    @Output() fileDeleted = new EventEmitter<any>();

    @ViewChild('fileUploadLabel', { read: ElementRef }) public _fileUploadLabel: ElementRef;
    @ViewChild('input', { read: ElementRef }) public _fileUploadElement: ElementRef;
    @ViewChild('fileDownload') public downloadLink: ElementRef;

    constructor(
        public readonly documentManagementService: DocumentManagementService,
        public _el: ElementRef,
        public readonly dialogService: DynamicDialogService,
        public readonly _authService: AuthService,
        public readonly _loader: LoaderService,
        public readonly contextService: ContextInteractionService
    ) {
        super(contextService);
    }

    public ngOnInit(): void {
        super.ngOnInit();
        this.formControl = this.formGroup.get(this.componentData.key);
        this.loadFilesFromContext();
        const context = this.getContextValue();
        if (context && this.componentData.config.useRadioButtons) {
            const option = this.componentData.config.radioOptions.find(o => o.id === context);
            if (option) {
                this.selectedOption = option;
            }
        }
    }

    public ngAfterViewInit(): void {
        window.addEventListener('drop', this._preventFileDrop.bind(this));
        window.addEventListener("dragover", this._preventDragOver.bind(this));
    }

    public ngOnDestroy(): void {
        if (this.fileSubscription) {
            this.fileSubscription.unsubscribe();
        }
        window.removeEventListener('drop', this._preventFileDrop);
        window.removeEventListener('dragover', this._preventDragOver);
    }

    /**
    * Handle style update when dragging over.
    * @param event
    */
    public onDragOver(event: DragEvent): boolean {
        this.hasFilesToDrop = true;
        return false;
    }

    /**
    * Handle style update when dragging out.
    * @param event
    */
    public onDragLeave(event: DragEvent): boolean {
        event.preventDefault();
        this.hasFilesToDrop = false;
        return false;
    }

    /**
    * Handle Drop of files.
    * @param event
    */
    public onDrop(event: DragEvent): void {
        event.preventDefault();
		this.hasFilesToDrop = false;
		let whiteListObs$: Observable<string[]> = (this._whitelist.length > 0)
			? of(this._whitelist)
			: this.documentManagementService.whiteList.pipe(
				tap((data) => {
					this._whitelist = data;
				})
			);
		let files = event.dataTransfer.files;
		this.fileSubscription.add(whiteListObs$.subscribe(
			(list) => {
				if (list && list.length > 0) {
					const extensions = [];					
					for (let fi = 0; fi < files.length; fi++) {
						const parts = files[fi].name.split(".");
						if (parts.length > 1) {
							extensions.push(parts[parts.length - 1]);
						} else {
							extensions.push("");
						}
					}
					const result = extensions.every(f => list.indexOf(f.toLowerCase()) !== -1);
					if (result) {
						this.addFilesToQueue(files);
					} else {
						this.disallowedDialog.message = this.whitelistMessage.replace("<000>", this._whitelist.join(', '));
						this.disallowedDialog.toggleDialog(true);
						this.dialogService.updateDialog(this.disallowedDialog);
					}
				}
			}, (err) => {
				console.error(err);
				this.dialogService.showError();
			}));
    }

    public addFiles(event: Event): void {
        this._loader.toggleLoader(true);
		const files = (event.srcElement as any).files;
		let whiteListObs$: Observable<string[]> = (this._whitelist.length > 0)
			? of(this._whitelist)
			: this.documentManagementService.whiteList.pipe(
				tap((data) => {
					this._whitelist = data;
				})
			);
		this.fileSubscription.add(whiteListObs$.subscribe(list => {
            if (list && list.length > 0) {
				const extensions = [];
				for (let fi = 0; fi < files.length; fi++) {
					const parts = files[fi].name.split(".");
					if (parts.length > 1) {
						extensions.push(parts[parts.length - 1]);
					} else {
						extensions.push("");
					}
            }
            this._loader.toggleLoader(false);
            const result = extensions.every(f => list.indexOf(f.toLowerCase()) !== -1);
            if (result) {
                this.addFilesToQueue(files);
			} else {
				this.disallowedDialog.message = this.whitelistMessage.replace("<000>", this._whitelist.join(', '));
                this.disallowedDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.disallowedDialog);
            }
            this._fileUploadElement.nativeElement.value = "";
            }
		}, (error) => { console.error(error); this.dialogService.showError(); })
		);      
    }

    public allowFileDeletion(file: IUploadFile): boolean {
        if (!this.isEnabled) return false;
    return (this.user.id === file.uploadedByUserId || this.user.organizationId === file.uploadedByOrganizationId);
    }

    public chooseFileLabelClick(event: Event) {
        event.stopPropagation();

        let chooseFileLabel = <HTMLLabelElement>this._el.nativeElement.querySelector(".file-select-label");
        if (chooseFileLabel)
            chooseFileLabel.click();
    }

    /**
    * Deletes the file from the list, and deletes the file from the server.
    * @param file
    */
    public deleteFile(file: IUploadFile): void {
        this.file = file;
        this.deleteDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.deleteDialog);
    }

    public downloadFile(file: IUploadFile): void {
        const download$ = this.documentManagementService.downloadBlob({ uri: file.blobUri, contentType: file.type, fileName: file.fileName } as BlobDownloadRequest).pipe(
            tap((data) => {
                this.renderBlob(data, file.type, file.fileName);
            }),
            finalize(() => { this.fileSubscription.add(download$); })
        ).subscribe(
            () => { },
            (error) => { console.error(error); }
        );
    }

    public optionChanged(e: RadioButtonOption): void {
        const current = this.getContextValue();
        this.selectedOption = e;
        if (this.selectedOption && this.selectedOption.id !== current) {
            this.setContextValue(this.selectedOption.id);
            this.validateControl();
        }
    }

    private renderBlob(data: any, contentType: string, fileName: string): void {
        const blob = new Blob([data], { type: contentType });
        let url = window.URL.createObjectURL(blob);
        const link = this.downloadLink.nativeElement;
        link.href = url;
        link.download = fileName;
        link.click();
    }

    public uploadFile(file: IUploadFile): Observable<IAzureBlobSasToken> {
        const uploadComplete = new Subject<IAzureBlobSasToken>();
        this.documentManagementService.provision(file.rawFile.name).subscribe(x => {
            this.documentManagementService.processProvisionedFile(file, x, uploadComplete);
		}, (error) => {
			if (this.documentManagementService.WHITELIST_ERRORS.indexOf(error.error) !== -1) {
					this.disallowedDialog.message = this.whitelistMessage.replace("<000>", this._whitelist.join(', '));
                    this.disallowedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.disallowedDialog);
                }
        });
        return uploadComplete.asObservable();
    }

    public addFilesToQueue(files: FileList): void {
        for (let i = 0; i < files.length; i++)
            if (!this._fileExists(files[i]))
                this.files.push({ rawFile: files[i], completed: false, waiting: true, isUploading: false, percentage: 0, fileName: files.item(i).name, type: files.item(i).type, uploadedByUserId: this.user.id, uploadedByOrganizationId: this.user.organizationId } as IUploadFile);
        this.processFiles();
    }

    public loadFilesFromContext(): void {
        let context = this.getContextValue();
        if (context && context.length > 0) {
            let option = this.componentData.config.radioOptions.findIndex(o => o.id === context);
            if (option === -1 && this.useRadioButtons) { //value in context.value is actually a list of files!
                //need to move any files in "value" over to "files"
                this.storeContext(context, "files");
                this.setContextValue("upload");
            } else {
                context = this.getContext("files");
            }
        } else {
            context = this.getContext("files");
        }
        
        if (!context)
            return;
        for (let file of context)
            this.files.push(file as IUploadFile);
        this.validateControl();
    }

    public _fileExists(file: File): boolean {
        for (var f of this.files) {
            if (f.rawFile.lastModified === file.lastModified && f.rawFile.name === file.name &&
            f.rawFile.size === file.size && f.rawFile.type === file.type) {
            return true;
            }
        }
        return false;
    }

    /**
    * Process files for upload.
    */
    public processFiles(): void {
        if (!this.files.length)
            return;
        const files = this.files.filter(x => !x.completed && !x.isUploading);
        if (files.length === 0)
            return;
        let obs: Observable<IAzureBlobSasToken>[] = [];
        files.forEach(x => {
            obs.push(this.uploadFile(x));
        });
        //since we can only upload 1 file at a time, use concat rather than merge
        observableConcat(...obs).subscribe(() => {
        }, () => {
            this.validateControl();
        }, () => {
            this.validateControl();
            if (this.fileUploaded !== undefined)
            this.fileUploaded.emit({ context: this.componentData.contextTarget, files: files });
    });
    }

    /**
    * Validates the control manually
    */
    public validateControl(): void {
        if (!this.componentData.contextTarget) {
            console.warn(`There is no context target configured - can't save fileId`);
            return;
        }
        const location = "files";
        let filteredFiles = this.files.filter(x => x.completed === false);
        if (this.files && this.files.length > 0 && filteredFiles.length == 0) {
            this.storeContext(this.files, location);
        }
        else {
            this.storeContext([], location);
        }
        let value: any = this.getContext(location);
        this.formControl.setValue(value);
        this.formControl.markAsTouched();
        this.formControl.markAsDirty();
        this._loader.toggleLoader(false);
        this.change();
    }

    public _preventDragOver(e): void {
        e.preventDefault();
    }

    public _preventFileDrop(e): void {
        if (this.isEdit || !e.composedPath().find(x => x == this._fileUploadLabel.nativeElement) && e.dataTransfer.files.length != 0) {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'none';
            e.dataTransfer.effectAllowed = 'none';
        }
    }

    public onDialogConfirm(): void {
        this.documentManagementService.abandon(this.file).pipe(first()).subscribe(() => {
            const index = this.files.findIndex(x => x.blobUri === this.file.blobUri);
            if (index !== -1)
                this.files.splice(index, 1);
            this.validateControl();
            this.fileDeleted.emit(this.file);
        }, (error) => { console.error(error); });
  }

    public onDialogClose(): void {
        console.log("dialog cancelled");
    }

}
